/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { JSX } from 'react';
import { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import IconButton from '@atlaskit/button/icon/button';
import { FULL_PAGE_EDITOR_TOOLBAR_HEIGHT } from '@atlaskit/editor-shared-styles/constants';
import CrossIcon from '@atlaskit/icon/core/cross';
import WarningIcon from '@atlaskit/icon/core/status-warning';
import { token } from '@atlaskit/tokens';

import {
	type DevDocsBylineConnectionAncestor,
	useDevDocsBylineConnection,
} from './byline-connection/devDocsConnections.ts';

export const DEV_DOCS_OVERWRITE_WARNING_BANNER_TEST_ID = 'dev-docs-overwrite-warning-banner';

const styles = cssMap({
	banner: {
		position: 'absolute',
		insetInlineStart: token('space.0'),
		width: '100%',
		zIndex: 300,
		boxSizing: 'border-box',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.100'),
		paddingBlock: token('space.075'),
		paddingInline: token('space.075'),
		backgroundColor: token('color.background.warning'),
	},
	text: {
		font: token('font.body'),
		color: token('color.text.warning'),
	},
	dismiss: {
		position: 'absolute',
		insetInlineEnd: token('space.100'),
		insetBlockStart: token('space.0'),
		insetBlockEnd: token('space.0'),
		display: 'flex',
		alignItems: 'center',
	},
});

const i18n = defineMessages({
	message: {
		id: 'dev-docs.overwrite-warning.message.non-final',
		defaultMessage: 'Changes to this content will be overwritten if the source content is updated.',
		description:
			'Warning shown in the page editor when the page is managed by a Dev Docs source, letting the author know their edits may be replaced the next time the source content syncs.',
	},
	dismiss: {
		id: 'dev-docs.overwrite-warning.dismiss.non-final',
		defaultMessage: 'Dismiss',
		description: 'Accessible label for the button that dismisses the overwrite warning banner.',
	},
	warningIcon: {
		id: 'dev-docs.overwrite-warning.warning-icon.non-final',
		defaultMessage: 'Warning',
		description:
			'Accessible label announcing the severity of the Dev Docs overwrite warning banner icon.',
	},
});

export type DevDocsOverwriteWarningBannerProps = {
	isLivePage: boolean;
	ancestors?: readonly DevDocsBylineConnectionAncestor[] | null;
};

export const DevDocsOverwriteWarningBanner = ({
	isLivePage,
	ancestors,
}: DevDocsOverwriteWarningBannerProps): JSX.Element | null => {
	const intl = useIntl();
	const connection = useDevDocsBylineConnection(ancestors);
	const [dismissed, setDismissed] = useState(false);

	const handleDismiss = useCallback(() => {
		setDismissed(true);
	}, []);

	if (isLivePage || !connection || dismissed) {
		return null;
	}

	return (
		<div
			role="status"
			data-testid={DEV_DOCS_OVERWRITE_WARNING_BANNER_TEST_ID}
			css={styles.banner}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- toolbar height is flag-dependent (AIFC vs default); must be resolved at runtime and applied inline
			style={{ insetBlockStart: FULL_PAGE_EDITOR_TOOLBAR_HEIGHT() }}
		>
			<WarningIcon
				label={intl.formatMessage(i18n.warningIcon)}
				color={token('color.icon.warning')}
			/>
			<span css={styles.text}>{intl.formatMessage(i18n.message)}</span>
			<span css={styles.dismiss}>
				<IconButton
					appearance="subtle"
					spacing="compact"
					icon={CrossIcon}
					label={intl.formatMessage(i18n.dismiss)}
					onClick={handleDismiss}
				/>
			</span>
		</div>
	);
};
